import React, { useRef, useCallback } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useEscapeKey } from "../hooks/useEscapeKey";
import { useClickOutside } from "../hooks/useClickOutside";

const dialogStyles = {
  backdrop: "fixed inset-0 bg-black/50 z-[60]",
  overlay: "fixed inset-0 z-[70] flex items-center justify-center p-4",
  content: "bg-card rounded-lg shadow-xl w-full flex flex-col",
  header: "flex items-center justify-between p-4 border-b border-border flex-shrink-0",
  title: "text-base font-semibold text-foreground",
  subtitle: "text-xs text-muted-foreground mt-0.5",
  closeButton: "p-1 text-muted-foreground hover:text-foreground rounded",
  body: "flex-1 overflow-y-auto p-4 bg-muted",
} as const;

interface DialogProps {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  /** Optional subtitle or description below the title */
  subtitle?: React.ReactNode;
  /** Max width class, e.g. "max-w-3xl" or "max-w-4xl" */
  maxWidth?: string;
  /** Max height class for content area, e.g. "max-h-[80vh]" or "max-h-[85vh]" */
  maxHeight?: string;
  /** ID for aria-labelledby (defaults to "dialog-title") */
  ariaLabelledBy?: string;
}

/**
 * A reusable modal dialog with backdrop, header, and close behavior.
 * Uses useEscapeKey and useClickOutside for accessibility.
 */
export function Dialog({
  title,
  children,
  onClose,
  subtitle,
  maxWidth = "max-w-3xl",
  maxHeight = "max-h-[80vh]",
  ariaLabelledBy = "dialog-title",
}: DialogProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  useEscapeKey(onClose);
  useClickOutside(contentRef, useCallback(() => onClose(), [onClose]));

  return (
    <>
      <div
        className={dialogStyles.backdrop}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={dialogStyles.overlay}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabelledBy}
        onClick={onClose}
      >
        <div
          ref={contentRef}
          className={`${dialogStyles.content} ${maxWidth} ${maxHeight}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={dialogStyles.header}>
            <div>
              <h2 id={ariaLabelledBy} className={dialogStyles.title}>
                {title}
              </h2>
              {subtitle && (
                <div className={dialogStyles.subtitle}>{subtitle}</div>
              )}
            </div>
            <button
              onClick={onClose}
              className={dialogStyles.closeButton}
              aria-label="Close"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          <div className={dialogStyles.body}>{children}</div>
        </div>
      </div>
    </>
  );
}
